import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { withStyles, setOpacity } from 'theme/withStyles';
import RawHtml from 'common/RawHtml';
import Parser from 'html-react-parser';
import Spinner from 'common/Spinner';
import resetStyles from 'theme/reset';
import ErrorAnnounce from 'a11y/ErrorAnnounce';
import MessageAnnounce from 'a11y/MessageAnnounce';
import { Branding, engageConfig } from '@ces/components';
import useGoogleRecaptchaBadge from 'hooks/useGoogleRecaptchaBadge';
import ApplyOtherMenu from 'pages/home/HomeApplyOtherMenu';
import Header from './components/Header';
import Dialog from './Dialog';

const activeBranding = window.localStorage.getItem('createTheme') === 'true';

const styles = ({ links, palette, screenSizes, spacing, typography }) => {
  const importFonts = typography?.fontFamilies?.urls?.map((font) => `url('${font}')`) ?? [];
  importFonts.push(
    `url('https://fonts.googleapis.com/css?family=Barlow+Condensed:100,100i,200,200i,300,300i,400,400i,500,500i,700,700i,800,800i&display=swap')`,
    `url('https://fonts.googleapis.com/css?family=Roboto:100,100i,200,200i,300,300i,400,400i,500,500i,700,700i,800,800i&display=swap')`,
    `url('https://fonts.googleapis.com/css?family=Allura|Inconsolata&display=swap')`,
  );

  const overlayStyles = {
    '&.active': {
      cursor: 'not-allowed',
      opacity: 1,
      pointerEvents: 'auto',
    },
    display: 'flex',
    height: '100%',
    left: 0,
    opacity: 0,
    pointerEvents: 'none',
    position: 'fixed',
    // Setting this to -1 causes a weird blinking artifact to appear in all text inputs
    top: 0,
    width: '100%',
    zIndex: 9999,
  };
  return {
    // @import needs to be at the top
    '@import': importFonts,
    // eslint-disable-next-line
    '@global': {
      ...resetStyles({ palette, screenSizes }),
      a: {
        '&:hover': {
          color: links.link.hoverColor,
          textDecoration: 'underline',
        },
        color: links.link.color,
        textDecoration: 'none',
      },
      b: {
        fontWeight: typography.fontWeights.bold,
      },
      body: {
        ...typography.p,
        ...typography.p1,
        background: palette.background,
        color: palette.text,
        overflowX: 'hidden',
      },
      em: {
        fontStyle: 'italic',
      },
      footer: {
        color: palette.text,
        fontSize: 14,
        margin: '0 auto',
        maxWidth: 900,
        padding: [spacing.sm, 0],
      },
      h1: typography.h1,
      h2: typography.h2,
      h3: typography.h3,
      h4: typography.h4,
      p: {
        ...typography.p,
        ...typography.p1,
        marginBottom: spacing.sm,
      },
      strong: {
        fontWeight: typography.fontWeights.bold,
      },
      ul: {
        listStyleType: 'disc',
        marginLeft: 20,
        paddingLeft: 20,
        textAlign: 'left',
      },
      [`@media(min-width: ${screenSizes.mobileMax}px)`]: {
        body: {
          fontSize: typography.p1.desktopFontSize,
        },
        p: {
          fontSize: typography.p1.desktopFontSize,
        },
      },
    },
    disableOverlay: {
      ...overlayStyles,
      background: 'hsla(0, 0%, 100%, 0)',
      transition: 'all 0.3s ease',
    },
    h1: {
      ...typography.h1,
      marginBottom: 10,
      textAlign: 'center',
      [`@media(min-width: ${screenSizes.mobileMax}px)`]: {
        marginBottom: 27,
      },
    },
    h2: {
      ...typography.p,
      ...typography.p1,
      fontWeight: typography.fontWeights.regular,
      marginBottom: 30,
      textAlign: 'center',
      [`@media(min-width: ${screenSizes.mobileMax}px)`]: {
        fontSize: typography.p1.desktopFontSize,
        marginBottom: 46,
      },
    },
    loadingWrapper: {
      ...overlayStyles,
      alignItems: 'center',
      background: setOpacity(palette.background, 0.7),
      justifyContent: 'center',
      transition: 'all 0.5s ease',
    },
    mainContent: {
      margin: '0 auto',
      maxWidth: screenSizes.desktopLgMin,
      paddingTop: spacing.xl,
      position: 'relative',
    },
    pageHeading: {
      textAlign: 'center',
    },
    root: {
      '& footer': {
        clear: 'both',
        marginTop: 100,
        [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
          padding: [0, spacing.xs],
        },
      },
      fontFamily: typography.p.fontFamily,
      fontWeight: typography.fontWeights.regular,
      minWidth: 320,
    },
    sideBarContent: {
      float: 'right',
      position: 'relative',
      zIndex: 10,
    },
    [`@media(max-width: ${screenSizes.mobileMax}px)`]: {
      mainContent: {
        paddingTop: spacing.lg,
      },
      root: {
        minWidth: 280,
      },
      sideBarContent: {
        display: 'none',
      },
    },
  };
};

const getWrapClass = () => {
  const regexAndroid = /Android/i;
  const regexIOS = /iPhone|iPad|iPod/i;
  const regexWindowsPhone = /Windows Phone/i;

  if (navigator.userAgent.match(regexAndroid)) {
    return 'isAndroid';
  }
  if (navigator.userAgent.match(regexIOS)) {
    return 'isIOS';
  }
  if (navigator.userAgent.match(regexWindowsPhone)) {
    return 'isWindowsPhone';
  }
  return 'isDesktop';
};

const Layout = ({
  activeGroup,
  activeGroupIndex,
  children,
  classes,
  clientLabel,
  dialogOpen,
  dialogType,
  footer,
  header,
  loading,
  loadingOverlayActive,
  onCloseDialog,
  pageName,
  productDisplayGroup,
  resources,
  sideBarContent,
  subtitle,
  title,
}) => {
  const showHeader =
    (pageName === 'launch' && activeGroup === null) ||
    (pageName !== 'launch' && activeGroupIndex === 0);

  useEffect(() => {
    document.title = `${clientLabel} - Apply Online`;
  }, [clientLabel, pageName, title]);

  useGoogleRecaptchaBadge();

  return (
    <>
      {dialogOpen && <Dialog type={dialogType} onClose={onCloseDialog} />}
      <div className={classnames('glu-content', classes.root)} style={{ display: 'block' }}>
        <div className={getWrapClass()}>
          <div className="container-fluid">
            <div
              className={classnames(
                'disable-overlay',
                { active: loadingOverlayActive },
                classes.disableOverlay,
              )}
            >
              &nbsp;
            </div>
            <MessageAnnounce
              show
              message={loading ? 'Content is loading' : 'Content has finished loading'}
              isRemoved
            />
            <div
              className={classnames(
                'loader-wrapper',
                {
                  active: loading,
                },
                classes.loadingWrapper,
              )}
            >
              <Spinner type="full" loading />
            </div>
            <div id="main" aria-hidden={dialogOpen}>
              <Header />

              <RawHtml className="header-html">{header}</RawHtml>

              {sideBarContent && (
                <RawHtml className={classnames('side-bar-content', classes.sideBarContent)}>
                  {sideBarContent}
                </RawHtml>
              )}

              <div role="main" className={classnames('main-content', classes.mainContent)}>
                {showHeader && (title || subtitle) && (
                  <div className={classnames('row page-heading', classes.pageHeading)}>
                    <div className="widget">
                      {title && (
                        <RawHtml type="h1" className={classes.h1}>
                          {title}
                        </RawHtml>
                      )}
                      {resources.applyLinkLabel && productDisplayGroup.length === 0 && (
                        <ApplyOtherMenu />
                      )}
                      {subtitle && (
                        <RawHtml type="h2" className={classes.h2}>
                          {subtitle}
                        </RawHtml>
                      )}
                    </div>
                  </div>
                )}
                <section>{children}</section>
              </div>

              {Parser(footer)}
            </div>
          </div>
        </div>
      </div>
      {activeBranding && <Branding createEditableParts={engageConfig} />}
      <ErrorAnnounce />
      {sideBarContent && (
        <RawHtml className={classnames('side-bar-content', classes.sideBarContent)}>
          {sideBarContent}
        </RawHtml>
      )}
    </>
  );
};

Layout.propTypes = {
  activeGroup: PropTypes.string,
  activeGroupIndex: PropTypes.number,
  children: PropTypes.node,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  clientLabel: PropTypes.string,
  dialogOpen: PropTypes.bool,
  dialogType: PropTypes.string,
  footer: PropTypes.string,
  header: PropTypes.string,
  loading: PropTypes.bool,
  loadingOverlayActive: PropTypes.bool.isRequired,
  onCloseDialog: PropTypes.func,
  pageName: PropTypes.string,
  productDisplayGroup: PropTypes.arrayOf(PropTypes.shape({})),
  resources: PropTypes.shape({
    applyLinkLabel: PropTypes.string,
  }).isRequired,
  sideBarContent: PropTypes.string,
  subtitle: PropTypes.string,
  title: PropTypes.string,
};

Layout.defaultProps = {
  activeGroup: null,
  activeGroupIndex: 0,
  children: null,
  clientLabel: '',
  dialogOpen: false,
  dialogType: null,
  footer: '',
  header: '',
  loading: false,
  onCloseDialog: () => null,
  pageName: '',
  productDisplayGroup: [],
  sideBarContent: '',
  subtitle: null,
  title: null,
};

const mapStateToProps = ({
  data,
  dialogs,
  groups,
  page,
  progress,
  refs,
  resources,
  session,
  ui,
}) => ({
  activeGroup: ui.activeGroup,
  activeGroupIndex: Object.values(groups)?.findIndex((group) => group.name === ui.activeGroup),
  clientLabel: session.data?.clientLabel,
  dialogOpen: dialogs.open,
  dialogType: dialogs.type,
  footer: session.meta?.footer?.html,
  header: session.meta?.header?.html,
  loading:
    page?.name === 'kba'
      ? // eslint-disable-next-line camelcase
        ui.appLoading && session.meta?.configs?.kba_spinny
      : // eslint-disable-next-line camelcase
        ui.appLoading && session.meta?.configs?.app_spinny,
  loadingOverlayActive: ui.appLoading || ui.pageLoading || ui.groupLoading || false,
  onCloseDialog: dialogs.params?.onClose,
  pageName: page.name,
  product: refs.product,
  productDisplayGroup: data.productDisplayGroup,

  progress,
  resources,
  // eslint-disable-next-line camelcase
  sideBarContent: resources.side_bar,
  // This should be removed after API for title/subtitle is completed
  subtitle: page.name === 'launch' ? page.subtitle || "Let's get started" : page.subtitle,
  title: page.name === 'launch' ? page.title || 'Select an option below to begin.' : page.title,
});

const LayoutStyled = withStyles(styles)(Layout);

export default connect(mapStateToProps)(LayoutStyled);
